import { CUSTOMER_TYPES, type CustomerInput } from './customers.api';

/**
 * Regulile pe care le aplică formularul de client **înainte** de a trimite.
 *
 * Modul separat de ecran, și nu pentru curățenie: astea sunt regulile care decid dacă
 * apăsarea pe Salvează face ceva. Într-un `.tsx` de o mie de linii n-ar fi putut fi testate
 * decât randând tot ecranul; aici sunt funcții pure, verificabile cu o listă de cazuri.
 *
 * ⚠️ **Nu țin loc de server.** Regula care contează e `customerWriteSchema`
 * (`libs/be/customers/src/contracts/customer.contract.ts`), pentru că API-ul are și alți
 * apelanți decât ecranul nostru. Copia de aici cumpără un singur lucru, dar important:
 * operatorul află *ce* lipsește apăsând Salvează, nu că nu se întâmplă nimic.
 */

/**
 * Câmpul → `id`-ul controlului lui în pagină.
 *
 * Serverul respinge pe numele câmpului din contract; ecranul trebuie să ducă operatorul la
 * el. Fără harta asta, un refuz pe „persoana de contact" ar picta un rând roșu la trei
 * ecrane sub marginea de jos, iar apăsarea pe Salvează ar arăta ca și cum n-ar face nimic.
 */
export const FIELD_ANCHORS: Partial<Record<keyof CustomerInput, string>> = {
  nickname: 'client-cod',
  name: 'client-denumire',
  companyId: 'client-firma',
  clientProfileId: 'client-profil',
  cnp: 'client-cnp',
  cif: 'client-cif',
  countryCode: 'client-tara',
  tradeRegisterNo: 'client-registru',
  representativeName: 'client-reprezentant',
  legalAddress: 'client-adresa',
  city: 'client-localitate',
  county: 'client-judet',
  email: 'client-email',
  phoneSales: 'client-tel-contact',
  phoneBilling: 'client-tel-facturare',
  billingEmail: 'client-email-facturare',
  phoneTechnical: 'client-tel-tehnic',
  phoneOther: 'client-tel-alt',
  contactPerson: 'client-contact',
  iban: 'client-iban',
  bankName: 'client-banca',
};

/**
 * Ce cere contractul, verificat înainte de a-l deranja.
 *
 * Mesajele sunt aceleași cu ale serverului, cuvânt cu cuvânt: altfel același câmp gol ar
 * primi două explicații diferite, după cum a fost prins de ecran sau de contract, iar
 * operatorul ar crede că sunt două probleme.
 *
 * Ordinea în care se pun aici e ordinea din formular — prima cheie e câmpul spre care se
 * derulează.
 */
export function validate(form: CustomerInput): Partial<Record<keyof CustomerInput, string>> {
  const found: Partial<Record<keyof CustomerInput, string>> = {};
  const isIndividual = form.customerType === CUSTOMER_TYPES.individual;

  if (form.nickname.trim().length === 0) {
    found.nickname = 'Codul de client e obligatoriu.';
  }

  if (form.name.trim().length === 0) {
    found.name = isIndividual ? 'Numele e obligatoriu.' : 'Denumirea e obligatorie.';
  }

  if (form.companyId.length === 0) {
    found.companyId = 'Alege firma din grup căreia îi aparține clientul.';
  }

  if (!isIndividual && form.cif.trim().length === 0) {
    found.cif = 'Codul fiscal e obligatoriu.';
  }

  if (form.customerType === CUSTOMER_TYPES.foreign && form.countryCode.trim().length !== 2) {
    found.countryCode = 'Codul de țară are două litere (ISO 3166-1), de ex. `BG`.';
  }

  if (!isIndividual && form.representativeName.trim().length === 0) {
    found.representativeName = 'Numele reprezentantului legal e obligatoriu.';
  }

  if (form.email.trim().length === 0) {
    found.email = 'E-mailul e obligatoriu — pe el pleacă factura și linkul de semnare.';
  }

  if (!isIndividual && form.phoneSales.trim().length === 0) {
    found.phoneSales = 'Telefonul de contact e obligatoriu.';
  }

  if (!isIndividual && form.contactPerson.trim().length === 0) {
    found.contactPerson = 'Persoana de contact e obligatorie.';
  }

  return found;
}
